import { useState } from "react";
import { NavLink, Outlet, useNavigate, Navigate, Link } from "react-router-dom";
import {
  LayoutDashboard,
  Car,
  Inbox,
  Image as ImageIcon,
  Settings as Cog,
  LogOut,
  ExternalLink,
  Menu,
  X,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/lib/auth";
import { TID } from "@/constants/tids";

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${
    isActive
      ? "bg-[#0A0A0A] text-white"
      : "text-gray-600 hover:bg-gray-100 hover:text-[#0A0A0A]"
  }`;

const items = [
  { to: "/admin", end: true, icon: LayoutDashboard, label: "Dashboard", tid: TID.adminDashboard },
  { to: "/admin/masini", icon: Car, label: "Mașini", tid: TID.adminCarsLink },
  { to: "/admin/solicitari", icon: Inbox, label: "Solicitări", tid: TID.adminInquiriesLink },
  { to: "/admin/media", icon: ImageIcon, label: "Media", tid: TID.adminMediaLink },
  { to: "/admin/setari", icon: Cog, label: "Setări", tid: TID.adminSettingsLink },
];

function SidebarContent({ admin, onLogout, onNavigate }) {
  return (
    <div className="h-full flex flex-col">
      <div className="h-20 px-6 flex items-center gap-2 border-b border-gray-100 shrink-0">
        <img
          src="/autorio-logo.jpg"
          alt="AutoRIO"
          className="h-10 w-10 rounded-full object-cover"
        />
        <div>
          <div className="text-base font-extrabold text-[#0A0A0A]">AutoRIO</div>
          <div className="text-[9px] uppercase tracking-[0.2em] text-gray-400">Panou Admin</div>
        </div>
      </div>

      <nav className="flex-1 px-4 py-6 space-y-1.5 overflow-y-auto">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            className={linkCls}
            data-testid={it.tid}
            onClick={onNavigate}
          >
            <it.icon className="h-4 w-4 shrink-0" />
            {it.label}
          </NavLink>
        ))}
      </nav>

      <div className="p-4 border-t border-gray-100 space-y-2 shrink-0">
        <Link
          to="/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-100 hover:text-[#0A0A0A] transition-colors"
        >
          <ExternalLink className="h-4 w-4" />
          Vezi site-ul
        </Link>
        <button
          data-testid={TID.adminLogout}
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors"
        >
          <LogOut className="h-4 w-4" />
          Deconectare
        </button>
        <div className="pt-2 text-[10px] text-gray-400 px-4 truncate">{admin?.email}</div>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const { admin, loading, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-sm text-gray-400">Se încarcă...</div>
      </div>
    );
  }
  if (!admin) return <Navigate to="/admin/login" replace />;

  const doLogout = () => {
    logout();
    nav("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Desktop sidebar */}
      <aside
        data-testid={TID.adminSidebar}
        className="hidden lg:flex fixed top-0 left-0 bottom-0 w-64 bg-white border-r border-gray-200 flex-col"
      >
        <SidebarContent admin={admin} onLogout={doLogout} />
      </aside>

      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-40 bg-white border-b border-gray-100 h-16 flex items-center px-4">
        <button
          onClick={() => setOpen(true)}
          className="h-11 w-11 flex items-center justify-center -ml-2 rounded-xl hover:bg-gray-100 active:bg-gray-200 transition-colors"
          aria-label="Deschide meniul"
          data-testid="admin-mobile-menu"
        >
          <Menu className="h-5 w-5" />
        </button>
        <Link to="/admin" className="flex items-center gap-2 ml-2">
          <img
            src="/autorio-logo.jpg"
            alt="AutoRIO"
            className="h-9 w-9 rounded-full object-cover"
          />
          <div>
            <div className="text-sm font-extrabold text-[#0A0A0A] leading-none">AutoRIO</div>
            <div className="text-[9px] uppercase tracking-[0.18em] text-gray-400 mt-0.5">
              Admin
            </div>
          </div>
        </Link>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <div className="lg:hidden fixed inset-0 z-50">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50"
              onClick={() => setOpen(false)}
            />
            <motion.aside
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "tween", duration: 0.25 }}
              className="absolute top-0 left-0 bottom-0 w-72 max-w-[85vw] bg-white shadow-2xl"
            >
              <button
                onClick={() => setOpen(false)}
                className="absolute top-4 right-4 h-10 w-10 rounded-lg hover:bg-gray-100 flex items-center justify-center z-10"
                aria-label="Închide"
              >
                <X className="h-5 w-5" />
              </button>
              <SidebarContent admin={admin} onLogout={doLogout} onNavigate={() => setOpen(false)} />
            </motion.aside>
          </div>
        )}
      </AnimatePresence>

      <main className="lg:ml-64 min-h-screen p-4 sm:p-6 lg:p-8">
        <Outlet />
      </main>
    </div>
  );
}
